import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { API, get } from "../../config";
import { decrypt, encrypt } from "../Misc";

// Create the TrustContext
const TrustContext = createContext();

// Create the TrustProvider component
export function TrustProvider({ children }) {
  const params = useParams();
  const location = useLocation();
  const trustID = params?.trust_id || "";

  const [trustId, setTrustId] = useState(trustID);
  const [trustData, setTrustData] = useState(null);
  const [trustList, setTrustList] = useState(null);
  const prevTrustIdRef = useRef(null); // ✅ Track the last fetched trustId

  // Load from localStorage on mount
  useEffect(() => {
    if (localStorage.getItem("trustData")) {
      const storedUser = decrypt(localStorage.getItem("trustData"));
      if (storedUser) {
        setTrustData(JSON.parse(storedUser));
      }
    }
  }, []);

  // Persist to localStorage on change
  useEffect(() => {
    if (trustData) {
      localStorage.setItem("trustData", encrypt(JSON.stringify(trustData)));
    } else {
      localStorage.removeItem("trustData");
    }
  }, [trustData]);

  useEffect(() => {
    if (
      location.pathname.includes("/trusts/") &&
      trustId &&
      trustId !== prevTrustIdRef.current // ✅ Avoid redundant API call
    ) {
      fetchTrustData(trustId);
    }
  }, [location.pathname, trustId]);

  const fetchTrustData = async (id) => {
    try {
      const { data } = await get(`${API.TRUST_DETAILS}/` + id);
      setTrustData(data?.data);
      prevTrustIdRef.current = id; // ✅ Update last fetched ID
    } catch (error) {
      console.error("Error fetching Trust data:", error);
    }
  };

  const fetchTrustList = async (orgId) => {
    try {
      if (orgId) {
        const { data } = await get(
          `${API.TRUST_DETAILS}?organization_id=${orgId}&limit=-1`
        );
        let list = data?.data?.data?.map((item) => {
          return {
            ...item,
            label: item?.business_name
              ? item?.business_name
              : item?.first_name
              ? item?.first_name + " " + item?.last_name
              : item?.last_name,
            value: item?.id,
          };
        });
        setTrustList(list);
      }
    } catch (error) {
      console.error("Error fetching Trust data:", error);
    }
  };

  // Function to update project data conditionally
  const updateTrustData = (newData) => {
    setTrustData((prevData) => (prevData !== newData ? newData : prevData));
  };

  const fetchTrustContext = () => {
    if (trustId && trustId !== prevTrustIdRef.current) {
      fetchTrustData(trustId);
    }
  };

  useEffect(() => {
    if (params?.trust_id && params.trust_id !== trustId) {
      setTrustId(params.trust_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.trust_id]);

  // Define the context value object
  const contextValue = {
    trustId,
    trustData,
    updateTrustData,
    fetchTrustContext,
    fetchTrustList,
    trustList,
  };

  return (
    <TrustContext.Provider value={contextValue}>
      {children}
    </TrustContext.Provider>
  );
}

// Custom hook to use the TrustContext
export function useTrust(org = false) {
  const context = useContext(TrustContext);
  if (org) {
    return { trustId: "" };
  }
  if (!context) {
    throw new Error("useTrust must be used within a TrustProvider");
  }
  return context;
}
